/**
 * Shared confirmation for trashing or permanently deleting a database.
 *
 * Every entry point (canvas context menu, edgeless delete key, sidebar) goes
 * through this module. The trash and the permanent deletion of a cloud table
 * name the apps that read it and what they show afterwards, warn without
 * blocking, and are then run by the server; an unknown usage is checked when
 * the server runs it. A local workspace has no apps and
 * trashes in the doc. Permanent deletion needs a server-authorized dependency
 * plan.
 */

import type { useConfirmModal } from '@affine/component';
import { I18n } from '@affine/i18n';
import type { DatabaseAppUsageRead } from '@blocksuite/affine/blocks/database';

import { DatabaseAppUsageRows } from '../native-app/database-app-usage-list';
import type {
  DatabaseDeleteDependencies,
  DatabaseRelationDependency,
} from './database-relation-dependencies';

export type DatabaseDeleteAction = 'trash' | 'permanent-delete';

export type DatabaseDeleteConfirmRequest = {
  databaseTitle: string;
  docTitle: string;
  /** Which apps read the table; null in a local workspace, which has none. */
  appUsage: DatabaseAppUsageRead | null;
} & (
  | { action: 'trash' }
  | { action: 'permanent-delete'; dependencies: DatabaseDeleteDependencies }
);

type ConfirmModalHandle = Pick<
  ReturnType<typeof useConfirmModal>,
  'openConfirmModal'
>;

function formatDependencyLine(dependency: DatabaseRelationDependency): string {
  return I18n.t('com.streamlain.streamlain.database-delete.dependency-line', {
    database:
      dependency.database.databaseTitle ||
      I18n.t('com.streamlain.streamlain.relation.restricted-database'),
    doc:
      dependency.database.docTitle ||
      I18n.t('com.streamlain.streamlain.relation.restricted-document'),
    columns: dependency.columns.map(column => column.name).join(', '),
  });
}

/**
 * The apps that read the table: listed with what they show afterwards, or
 * said to be checked later. Warns only; it never blocks the deletion.
 */
function DeleteAppUsage({
  appUsage,
  action,
}: {
  appUsage: DatabaseAppUsageRead;
  action: DatabaseDeleteAction;
}) {
  if (appUsage.status === 'unknown') {
    return (
      <p data-testid="database-delete-usage-unknown">
        {I18n.t('com.streamlain.streamlain.database-delete.usage-unknown')}
      </p>
    );
  }
  if (appUsage.usages.length === 0) return null;
  return (
    <>
      <p data-testid="database-delete-usage-lead">
        {I18n.t(
          action === 'trash'
            ? 'com.streamlain.streamlain.database-delete.usage-lead-trash'
            : 'com.streamlain.streamlain.database-delete.usage-lead-deleted'
        )}
      </p>
      <DatabaseAppUsageRows usages={appUsage.usages} />
    </>
  );
}

function DependencyList({
  dependencies,
}: {
  dependencies: DatabaseRelationDependency[];
}) {
  return (
    <ul data-testid="database-delete-dependency-list">
      {dependencies.map(dependency => (
        <li
          key={`${dependency.database.docId}:${dependency.database.blockId}`}
          data-testid="database-delete-dependency"
        >
          {formatDependencyLine(dependency)}
        </li>
      ))}
    </ul>
  );
}

export function DatabaseDeleteDescription({
  request,
}: {
  request: DatabaseDeleteConfirmRequest;
}) {
  const { action, databaseTitle, docTitle } = request;
  const dependencies =
    request.action === 'permanent-delete'
      ? request.dependencies.dependencies
      : [];
  const writable = dependencies.filter(entry => entry.writable);
  const readOnly = dependencies.filter(entry => !entry.writable);
  const baseMessage =
    action === 'trash'
      ? I18n.t('com.streamlain.streamlain.database-delete.trash-message', {
          name: databaseTitle,
        })
      : I18n.t('com.streamlain.streamlain.common.delete-permanently-message', {
          name: databaseTitle,
          docTitle,
        });

  return (
    <div data-testid="database-delete-description">
      <p>{baseMessage}</p>
      {request.appUsage && (
        <DeleteAppUsage appUsage={request.appUsage} action={action} />
      )}
      {action === 'trash' && (
        <p>
          {I18n.t(
            'com.streamlain.streamlain.database-delete.dependencies-trash'
          )}
        </p>
      )}
      {action === 'permanent-delete' && writable.length > 0 && (
        <>
          <p>
            {I18n.t(
              'com.streamlain.streamlain.database-delete.dependencies-permanent',
              { count: writable.length }
            )}
          </p>
          <DependencyList dependencies={writable} />
        </>
      )}
      {action === 'permanent-delete' && readOnly.length > 0 && (
        <>
          <p>
            {I18n.t(
              'com.streamlain.streamlain.database-delete.dependencies-readonly',
              { count: readOnly.length }
            )}
          </p>
          <DependencyList dependencies={readOnly} />
        </>
      )}
    </div>
  );
}

/**
 * Open the shared delete confirmation. Resolves `true` when the user confirms.
 */
export function confirmDatabaseDelete(
  modal: ConfirmModalHandle,
  request: DatabaseDeleteConfirmRequest
): Promise<boolean> {
  return new Promise<boolean>(resolve => {
    let settled = false;
    const settle = (value: boolean) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    modal.openConfirmModal({
      title:
        request.action === 'trash'
          ? I18n.t('com.streamlain.streamlain.database-delete.trash-title')
          : I18n.t('com.streamlain.streamlain.common.delete-permanently-title'),
      description: <DatabaseDeleteDescription request={request} />,
      confirmText:
        request.action === 'trash'
          ? I18n.t('com.streamlain.streamlain.common.move-to-trash')
          : I18n.t('com.streamlain.streamlain.common.delete-permanently'),
      cancelText: I18n.t('Cancel'),
      confirmButtonOptions: {
        variant: 'error',
        disabled:
          request.action === 'permanent-delete' &&
          request.dependencies.dependencies.some(entry => !entry.writable),
        ...({ 'data-testid': 'database-delete-confirm' } as Record<
          string,
          string
        >),
      },
      onConfirm: () => settle(true),
      onCancel: () => settle(false),
      onOpenChange: open => {
        if (!open) settle(false);
      },
    });
  });
}
